import { Group, TextInput, Box, Button, LoadingOverlay, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { AppDataService } from '../../services/AppDataService';
import useInfoModal from '../../hooks/useInfoModal';
import { MODAL_TYPE_ENUM } from '../../enums/CommonEnums';
import { useState } from 'react';
import { EditSubmissionFormValidations } from './validations/FormValidations';

const ContainerSubmissionEditForm = ({ submissionRecord, onSubmit }) => {
    const openModal = useInfoModal();
    const [loading, setLoading] = useState(false);

    // Keep track of materialType in state to conditionally add Wine to beverage list
    const [materialType, setMaterialType] = useState(submissionRecord.materialType || '');

    const form = useForm({
        initialValues: submissionRecord,
        validate: {
            productName: EditSubmissionFormValidations.productName,
            beverageType: EditSubmissionFormValidations.beverageType,
            size: EditSubmissionFormValidations.size,
            materialType: EditSubmissionFormValidations.materialType,
            upc: EditSubmissionFormValidations.upc,
            color: EditSubmissionFormValidations.color,
        },
    });

    // Base beverage list WITHOUT Wine
    const baseBeverageTypes = [
        { value: 'Beer', label: 'Beer' },
        { value: 'Cider', label: 'Cider' },
        { value: 'Coffee', label: 'Coffee' },
        { value: 'Dairy', label: 'Dairy' },
        { value: 'Energy', label: 'Energy' },
        { value: 'Juice', label: 'Juice' },
        { value: 'Mixer', label: 'Mixer' },
        { value: 'Soda', label: 'Soda' },
        { value: 'Sports', label: 'Sports' },
        { value: 'Tea', label: 'Tea' },
        { value: 'Water', label: 'Water' },
    ];

    // If materialType is 'Aluminum', add Wine to the list
    const beverageTypeData =
        materialType === 'Aluminum'
            ? [...baseBeverageTypes, { value: 'Wine', label: 'Wine' }]
            : baseBeverageTypes;

    // JSX component for wine considerations
    const WineConsiderations = () => (
        <div>
            <h3>Beverages That Qualify As Wine</h3>
            <ul>
                <li>Table/Still Wine</li>
                <li>Sparkling Wine/champagne</li>
                <li>Wine Spritzer</li>
                <li>Mead</li>
                <li>Sake</li>
                <li>Fortified wine</li>
                <li>Cider over 8.5% ABV</li>
            </ul>
        </div>
    );

    const handleFormSubmit = async (values) => {
        try {
            setLoading(true);

            const webResp = await AppDataService.UpdateUserContainerRegistration({
                registration: { id: submissionRecord.id, ...form.values },
            });
            const responseData = webResp.getResponse();

            if (webResp.hasError()) {
                setLoading(false);
                return openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, responseData.error.message);
            }

            onSubmit(responseData.data.registration);
            setLoading(false);
        } catch (err) {
            setLoading(false);
            openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
        }
    };

    return (
        <Box mx="auto">
            <LoadingOverlay visible={loading} overlayBlur={2} />

            <Group mt="xs" spacing="xs" align="flex-start">
                <TextInput
                    maw={150}
                    size="xs"
                    placeholder="UPC/EAN"
                    {...form.getInputProps('upc')}
                />
                <TextInput
                    maw={150}
                    size="xs"
                    placeholder="Product Brand & Name"
                    {...form.getInputProps('productName')}
                />
                <Select
                    withinPortal
                    dropdownPosition="bottom"
                    size="xs"
                    placeholder="Material"
                    // When the material changes, update the form value and local state.
                    // If the new material is not Aluminum and the beverage type is Wine, reset beverage type.
                    onChange={(value) => {
                        form.setFieldValue('materialType', value);
                        setMaterialType(value);
                        if (value !== 'Aluminum' && form.values.beverageType === 'Wine') {
                            form.setFieldValue('beverageType', '');
                        }
                    }}
                    value={form.values.materialType}
                    data={[
                        { value: 'Aluminum', label: 'Aluminum' },
                        { value: 'Plastic', label: 'Plastic' },
                        { value: 'Glass', label: 'Glass' },
                    ]}
                />
                <TextInput
                    maw={75}
                    size="xs"
                    placeholder="Size (oz)"
                    {...form.getInputProps('size')}
                />
                <Select
                    withinPortal
                    dropdownPosition="bottom"
                    size="xs"
                    placeholder="Beverage Type"
                    data={beverageTypeData}
                    // When beverage type changes, set the value.
                    // If Wine is selected, open the wine considerations modal.
                    onChange={(value) => {
                        form.setFieldValue('beverageType', value);
                        if (value === 'Wine') {
                            setTimeout(() => {
                                openModal(MODAL_TYPE_ENUM.GENERAL_INFO, <WineConsiderations />);
                            }, 0);
                        }
                    }}
                    value={form.values.beverageType}
                />
                <Select
                    withinPortal
                    dropdownPosition="bottom"
                    size="xs"
                    placeholder="Color (if glass or plastic)"
                    {...form.getInputProps('color')}
                    data={[
                        { value: 'Amber', label: 'Amber' },
                        { value: 'Clear', label: 'Clear' },
                        { value: 'Green', label: 'Green' },
                    ]}
                />
            </Group>

            <Group position="center" mt="md">
                <Button color="dark" compact onClick={form.onSubmit(handleFormSubmit)}>
                    Submit
                </Button>
                <Button color="dark" compact onClick={() => form.reset()}>
                    Reset
                </Button>
            </Group>
        </Box>
    );
};

export default ContainerSubmissionEditForm;
